import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { Layout } from "../UI/Layout/Layout.tsx";
import { useAllFranchisees } from "./useAllFranchisees.ts";
import { useToCreateFranchisee } from "./useToCreateFranchisee.ts";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { FranchiseeTable } from "./FranchiseeTable.tsx";
import { useToEditFranchisee } from "./useToEditFranchisee.ts";
import { Card } from "../UI/Card.tsx";
import { DebouncedTextInput } from "../UI/DebouncedTextInput.tsx";
import { useCallback, useMemo, useState } from "react";
import { useAllUserGroups } from "../UserGroup/useAllUserGroups.ts";
import { Select } from "@normasteaching/norma-ui";
import { useNavigate } from "react-router-dom";
import { BookedClassRoomsScreenPath } from "../BookedClassRooms/BookedClassRoomsScreen.tsx";
import { OutlinedButton } from "../UI/OutlinedButton.tsx";
import { IconButton } from "../UI/IconButton.tsx";
import {
  CalendarDaysIcon,
  ChatBubbleLeftEllipsisIcon,
  PlusIcon,
  SignalIcon,
} from "@heroicons/react/24/outline";
import { OnboardingLivesScreenPath } from "../OnboardingLive/OnboardingLivePaths.ts";
import { TeacherRole, TeacherTag } from "../gql/graphql.ts";
import { useTeacherCoordinatorReviews } from "./TeacherCoordinatorReview/useTeacherCoordinatorReviews.ts";
import {
  isInNotificationWindow,
  teacherNeedsReview,
} from "./TeacherCoordinatorReview/coordinatorReviewPeriod.ts";
import { TeacherCoordinatorReviewScreenPath } from "./TeacherCoordinatorReview/TeacherCoordinatorReviewScreen.tsx";
import { useTeacherScores } from "./TeacherCoordinatorReview/useTeacherScores.ts";
import { useNewTeacherIds } from "./TeacherCoordinatorReview/useNewTeacherIds.ts";
import { useTeacherStudentReviewCounts } from "./TeacherCoordinatorReview/useTeacherStudentReviewCounts.ts";
import { useTeacherTableUrlState } from "./useTeacherTableUrlState.ts";
import { matchesRange } from "../UI/Table/numberRange.ts";
import { useTeachersWithMissingFeedback } from "./TeacherMissingFeedback/useTeachersWithMissingFeedback.ts";
import { TeacherMissingFeedbackScreenPath } from "./TeacherMissingFeedback/TeacherMissingFeedbackScreen.tsx";

export const AllFranchiseeScreenPath = "/teachers";

function FilterToggle({
  label,
  active,
  onToggle,
}: {
  label: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className={[
        "px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
        active
          ? "bg-slate-800 text-white border-slate-800"
          : "bg-white text-slate-600 border-slate-300 hover:border-slate-500",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

export function FranchiseeScreen() {
  const { franchisees, loading } = useAllFranchisees();
  const { franchisees: internalFranchisees } = useAllFranchisees({
    isInternal: true,
  });
  const internalIds = useMemo(
    () => new Set(internalFranchisees.map((f) => f.id)),
    [internalFranchisees],
  );
  const { toCreateFranchisee } = useToCreateFranchisee();
  const { toEditFranchisee } = useToEditFranchisee();
  const { userGroups } = useAllUserGroups();
  const navigate = useNavigate();
  const { teachersWithMissingFeedback } = useTeachersWithMissingFeedback();
  const onboardingLivesButton = (
    <IconButton
      label={"Live onboarding"}
      icon={SignalIcon}
      onClick={() => navigate(OnboardingLivesScreenPath)}
    />
  );
  const missingFeedbacksButton = (
    <IconButton
      label={"Missing feedbacks"}
      icon={ChatBubbleLeftEllipsisIcon}
      onClick={() => navigate(TeacherMissingFeedbackScreenPath)}
      hasAlert={teachersWithMissingFeedback.length > 0}
    />
  );

  const franchiseeIds = useMemo(
    () => franchisees.map((f) => f.id),
    [franchisees],
  );
  const { reviewByTeacherId } = useTeacherCoordinatorReviews(franchiseeIds);
  const { scoreByTeacherId } = useTeacherScores(franchiseeIds);
  const { reviewCountByTeacherId } =
    useTeacherStudentReviewCounts(franchiseeIds);
  const {
    sort: tableSort,
    filters: tableFilters,
    setSort: setTableSort,
    setFilters: setTableFilters,
  } = useTeacherTableUrlState();

  const newTeacherChecks = useMemo(
    () =>
      franchisees
        .filter((f) => f.createdAt)
        .map((f) => ({ teacherId: f.id, createdAt: f.createdAt as string })),
    [franchisees],
  );
  const { newTeacherIds } = useNewTeacherIds(newTeacherChecks);

  const teachersWithMissingReview = useMemo(
    () =>
      franchisees.filter((f) =>
        teacherNeedsReview(
          f.createdAt,
          reviewByTeacherId.get(f.id)?.reviewedAtUtc,
        ),
      ),
    [franchisees, reviewByTeacherId],
  );

  const showReviewBanner =
    isInNotificationWindow() && teachersWithMissingReview.length > 0;

  const [searchText, setSearchText] = useState("");
  const [oneToOneActive, setOneToOneActive] = useState(false);
  const [groupActive, setGroupActive] = useState(false);
  const [internalActive, setInternalActive] = useState(false);
  const [onboardingLiveActive, setOnboardingLiveActive] = useState(false);
  const [normaStaffActive, setNormaStaffActive] = useState(false);
  const [newActive, setNewActive] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const onSearchChange = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  function toggleOneToOne() {
    setOneToOneActive((prev) => !prev);
  }

  function toggleGroup() {
    const next = !groupActive;
    setGroupActive(next);
    if (!next) setSelectedGroupId(null);
  }

  const filtered = useMemo(() => {
    return franchisees.filter((f) => {
      if (searchText) {
        const q = searchText.toLowerCase().replace(/[\s\-().+]/g, "");
        const phoneDigits = f.phoneNumber
          ? `${f.phoneNumber.countryCode}${f.phoneNumber.number}`.replace(
              /[\s\-().+]/g,
              "",
            )
          : "";
        const numberOnlyDigits =
          f.phoneNumber?.number.replace(/[\s\-().+]/g, "") ?? "";
        const fullName = `${f.givenName} ${f.familyName}`.toLowerCase();
        const matches =
          fullName.includes(searchText.toLowerCase()) ||
          f.email.toLowerCase().includes(searchText.toLowerCase()) ||
          (q.length > 0 && phoneDigits.includes(q)) ||
          (q.length > 0 && numberOnlyDigits.includes(q));
        if (!matches) return false;
      }

      if (oneToOneActive && !f.lesson1on1) return false;
      if (internalActive && !internalIds.has(f.id)) return false;
      if (onboardingLiveActive && !f.tags.includes(TeacherTag.OnboardingLive))
        return false;
      if (normaStaffActive && !f.roles.includes(TeacherRole.NormaStaff))
        return false;
      if (newActive && !newTeacherIds.has(f.id)) return false;

      if (groupActive) {
        if (!f.userGroupIds || f.userGroupIds.length === 0) return false;
        if (selectedGroupId && !f.userGroupIds.includes(selectedGroupId))
          return false;
      }

      const finalScore = scoreByTeacherId.get(f.id)?.finalScore;
      if (!matchesRange(finalScore, tableFilters.finalScore)) return false;

      const reviewCount = reviewCountByTeacherId.get(f.id);
      if (!matchesRange(reviewCount, tableFilters.reviewCount)) return false;

      return true;
    });
  }, [
    franchisees,
    searchText,
    oneToOneActive,
    groupActive,
    selectedGroupId,
    internalActive,
    internalIds,
    onboardingLiveActive,
    normaStaffActive,
    newActive,
    newTeacherIds,
    scoreByTeacherId,
    reviewCountByTeacherId,
    tableFilters,
  ]);

  const userGroupOptions = userGroups.map((g) => ({ name: g.name, id: g.id }));

  return (
    <Layout>
      {showReviewBanner && (
        <div
          className={
            "mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
          }
        >
          <div className={"flex items-center gap-2"}>
            <span className={"text-amber-600 font-medium text-sm"}>
              ⚠ {teachersWithMissingReview.length} teacher
              {teachersWithMissingReview.length > 1 ? "s are" : " is"} missing a
              coordinator review for this period.
            </span>
          </div>
          <button
            onClick={() => navigate(TeacherCoordinatorReviewScreenPath)}
            className={
              "text-sm font-medium text-amber-700 underline hover:text-amber-900"
            }
          >
            Go to reviews
          </button>
        </div>
      )}
      <Card>
        <div className={"flex items-center justify-between gap-3"}>
          <h1 className={"font-bold text-2xl"}>Teachers</h1>
          <div className={"hidden md:flex items-center gap-3"}>
            {onboardingLivesButton}
            {missingFeedbacksButton}
            <OutlinedButton
              label={"Check Booked Classrooms"}
              onClick={() => navigate(BookedClassRoomsScreenPath)}
            />
            <PrimaryButton
              label={"New Teacher"}
              onClick={() => toCreateFranchisee()}
            />
          </div>
          <div className={"flex md:hidden items-center gap-2"}>
            {onboardingLivesButton}
            {missingFeedbacksButton}
            <IconButton
              label={"Check Booked Classrooms"}
              icon={CalendarDaysIcon}
              onClick={() => navigate(BookedClassRoomsScreenPath)}
            />
            <IconButton
              label={"New Teacher"}
              icon={PlusIcon}
              onClick={() => toCreateFranchisee()}
            />
          </div>
        </div>
      </Card>
      <div className={"mt-4"}>
        <Card>
          <div className={"mb-1 flex items-center justify-between"}>
            <span className={"text-sm font-medium text-slate-500"}>
              {filtered.length} {filtered.length === 1 ? "teacher" : "teachers"}
              {filtered.length < franchisees.length
                ? ` of ${franchisees.length}`
                : ""}
            </span>
          </div>
          <div className={"mb-4 flex flex-col space-y-3"}>
            <DebouncedTextInput
              initialValue={searchText}
              debounceInMs={300}
              onChange={onSearchChange}
              placeholder={"Search by name, surname, email or phone"}
            />

            <div className={"flex flex-col space-y-2"}>
              <div className={"flex items-center gap-2"}>
                <span className={"text-sm font-medium text-slate-500"}>
                  Classi:
                </span>
                <FilterToggle
                  label={"1to1"}
                  active={oneToOneActive}
                  onToggle={toggleOneToOne}
                />
                <FilterToggle
                  label={"Group"}
                  active={groupActive}
                  onToggle={toggleGroup}
                />
                <FilterToggle
                  label={"Internal"}
                  active={internalActive}
                  onToggle={() => setInternalActive((prev) => !prev)}
                />
                <FilterToggle
                  label={"Live Onboarding"}
                  active={onboardingLiveActive}
                  onToggle={() => setOnboardingLiveActive((prev) => !prev)}
                />
                <FilterToggle
                  label={"Staff Norma"}
                  active={normaStaffActive}
                  onToggle={() => setNormaStaffActive((prev) => !prev)}
                />
                <FilterToggle
                  label={"New"}
                  active={newActive}
                  onToggle={() => setNewActive((prev) => !prev)}
                />
              </div>

              {groupActive && (
                <div className={"pl-2 max-w-xs"}>
                  <Select<string>
                    placeholder={"User group"}
                    options={userGroupOptions}
                    value={selectedGroupId ?? undefined}
                    onChange={(v) => setSelectedGroupId(v ?? null)}
                    clearable
                  />
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <LoadingRows rows={8} />
          ) : (
            <FranchiseeTable
              editFranchisee={(f) => toEditFranchisee(f.id)}
              franchisees={filtered}
              internalIds={internalIds}
              teachersWithMissingReviewIds={
                new Set(teachersWithMissingReview.map((f) => f.id))
              }
              scoreByTeacherId={scoreByTeacherId}
              newTeacherIds={newTeacherIds}
              reviewCountByTeacherId={reviewCountByTeacherId}
              sort={tableSort}
              onSortChange={setTableSort}
              filters={tableFilters}
              onFiltersChange={setTableFilters}
            />
          )}
        </Card>
      </div>
    </Layout>
  );
}
